import type { EditorCatalog } from './es'

export const en = {
  'load.loading': 'Opening the course…',
  'load.error': 'The course could not be opened. Try again in a few minutes.',
  'load.retry': 'Try again',
  'load.notFound': 'This course does not exist or is no longer available.',
  'load.archived': 'This course is archived and can no longer be edited.',
  'load.importPending.catalog':
    'This course has an imported version that has not been published yet. Publish it with the operator tool before editing it here.',
  'load.importPending.organization':
    'This course has a version that Eklevo imported and has not published yet. You can edit it once Eklevo publishes it.',
  'load.notAllowed': 'You cannot edit this course.',

  'create.title': 'Create a new course',
  'create.body': 'It is created as a draft, without lessons: nobody sees it until it is published.',
  'create.name': 'Course name',
  'create.description': 'Description',
  'create.series.hint':
    'Optional, with the level. Lowercase letters, numbers and hyphens, for example “leadership”.',
  'create.submit': 'Create and start editing',
  'create.busy': 'Creating…',
  'create.cancel': 'Cancel',
  'create.error': 'The course could not be created. Try again in a few minutes.',
  'create.error.limit':
    'Your organization has reached the maximum number of its own courses. To create another, discard a draft you do not need or write to hola@eklevo.com.',

  'page.breadcrumb': 'Breadcrumb',
  'page.breadcrumb.edit': 'Edit',
  'page.untitled': 'Untitled course',
  'page.status.draft': 'Draft',
  'page.status.published': 'Published · version {version, number}',

  'outline.title': 'Course content',
  'outline.empty': 'There are no lessons yet.',
  'outline.lesson': 'Lesson {number, number}: {title}',
  'outline.lesson.untitled': 'Lesson {number, number}: Untitled',
  'outline.intermediate': 'Intermediate assessment: {title}',
  'outline.intermediate.untitled': 'Intermediate assessment',
  'outline.final': 'Final assessment: {title}',
  'outline.final.untitled': 'Final assessment',
  'outline.hasProblems': 'Has fields to review',
  'outline.add': 'Add lesson',
  'outline.addIntermediate': 'Add an intermediate assessment after lesson {number, number}',
  'outline.intermediateTaken':
    'Lesson {number, number} already has an intermediate assessment after it.',
  'outline.addFinal': 'Add a final assessment',
  'outline.finalBlocked':
    'The last lesson already has an intermediate assessment after it: move it or delete it to add the final one.',
  'outline.full': 'A course has at most {max, number} lessons.',
  'outline.drag': 'Drag to move',
  'outline.moveUp': 'Move lesson {number, number} up',
  'outline.moveDown': 'Move lesson {number, number} down',
  'outline.remove': 'Delete lesson {number, number}',
  'outline.removeAssessment': 'Delete “{name}”',
  'outline.moved': '“{title}” moved to position {number, number} of {count, number}.',
  'outline.moved.untitled':
    'The untitled lesson moved to position {number, number} of {count, number}.',

  'general.title': 'General information',
  'general.courseTitle': 'Course title',
  'general.description': 'Short description',
  'general.description.count': '{count, number}/{max, number} characters',
  'general.longDescription': 'Course description',
  'general.longDescription.hint':
    'Optional. What the course is about, in more detail: shown on the course page, under the short description.',
  'general.objectives': 'Course objectives',
  'general.objectives.hint':
    'Optional. What people will be able to do when they finish the course. Shown on the course page.',
  'general.language': 'Course language',
  'general.language.newVersion':
    'Changing the language of a published course makes a new version when you publish: only new assignments get it.',
  'general.series': 'Series',
  'general.series.hint':
    'Optional. The courses of one series are its levels. Lowercase letters, digits and hyphens, for example “leadership”.',
  'general.level': 'Level',
  'general.level.none': 'No level',
  'general.level.option': 'Level {level, number}',
  'general.level.hint': 'Optional. It goes with the series: both or neither.',
  'general.minutes': 'Estimated time to complete (minutes)',
  'general.department': 'Department',
  'general.department.none': 'No department',
  'general.department.hint': 'The department that runs this programme. Saved when you choose it.',
  'general.department.saving': 'Saving the department…',
  'general.department.error': 'The department could not be saved. Choose it again.',

  'number.invalid': 'Enter a whole number from {min, number} to {max, number}.',
  'format.hint': 'You can use **bold**, *italics* and lists with “-”.',

  'language.es': 'Spanish',
  'language.ca': 'Catalan',
  'language.en': 'English',

  'lesson.heading': 'Lesson {number, number}',
  'lesson.none.title': 'This course has no lessons yet',
  'lesson.none.body': 'Add the first lesson to start writing its content.',
  'lesson.title': 'Lesson title',
  'lesson.description': 'Lesson description',
  'lesson.objectives': 'Lesson objectives',
  'lesson.objectives.hint': 'Optional. What is learnt in this lesson.',
  'lesson.keyIdeas': 'Key ideas',
  'lesson.keyIdeas.hint':
    "Optional. Only Noa reads them, in companies that have it turned on: people don't see them.",
  'lesson.optional': 'Optional.',
  'lesson.minutes': 'Duration (minutes)',
  'lesson.remove': 'Delete lesson',
  'lesson.assessment.title': 'Intermediate assessment',
  'lesson.assessment.none':
    "An intermediate assessment is a graded quiz that comes right after this lesson: it has to be passed to go on to the next one. It has its own questions, which are not taken from the lesson's test.",
  'lesson.assessment.add': 'Add an intermediate assessment',
  'lesson.assessment.has': 'After this lesson comes “{title}”.',
  'lesson.assessment.hasUntitled':
    'After this lesson comes an intermediate assessment without a title.',
  'lesson.assessment.open': 'Edit the assessment',
  'lesson.assessment.last': 'After the last lesson comes the final assessment.',

  'video.title': 'Video',
  'video.source': 'Video source',
  'video.source.file': 'Upload a file',
  'video.source.external': 'External link',
  'video.upload.formats': 'Accepted formats: MP4 (H.264). Maximum size: 500 MB.',
  'video.upload.choose': 'Choose video',
  'video.file.waiting': 'Video without a file: the upload did not finish.',
  'video.file.again': 'Upload the video again',
  'video.file': 'Uploaded video',
  'video.file.captions': 'With captions.',
  'video.file.noCaptions': 'Without captions.',
  'captions.title': 'Captions',
  'captions.formats': 'WebVTT format (.vtt), up to 2 MB. Optional.',
  'captions.choose': 'Add captions',
  'captions.file': 'Uploaded captions.',
  'captions.waiting': 'Captions without a file: the upload did not finish.',
  'captions.again': 'Upload the captions again',
  'captions.remove': 'Remove captions',
  'video.file.replace':
    'To change it for another video or for a YouTube or Vimeo link, remove the uploaded video first.',
  'video.remove': 'Remove video',
  'video.newVersion':
    "Adding a video to this lesson or removing it changes the course's structure: when published, it will be a new version. Changing the link, the file or the captions of a video is a correction.",
  'video.link': 'YouTube or Vimeo link',
  'video.link.placeholder': 'https://www.youtube.com/watch?v=…',
  'video.link.youtube': 'YouTube video.',
  'video.link.vimeo': 'Vimeo video.',
  'video.link.unknown':
    'This link is not on YouTube or Vimeo: only YouTube or Vimeo videos, over https, can be published.',
  'practice.title': 'Practice',
  'practice.body':
    'Up to {max, number} activities to put into practice what was seen. In the lesson, they come after the video. They do not count to complete it.',
  'practice.empty': 'This lesson has no practice yet.',
  'practice.activity': 'Activity {number, number}',
  'practice.kind': 'Type',
  'practice.kind.identification': 'Identification',
  'practice.kind.case': 'Case study',
  'practice.kind.noa': 'Practise with Noa',
  'practice.kind.other': 'Other',
  'practice.kind.noa.hint':
    'In the portal, this activity only appears in companies that have Noa turned on, with the “Practise with Noa” button.',
  'practice.name': 'Activity title',
  'practice.instructions': 'Instructions',
  'practice.instructions.hint': 'What the person has to do.',
  'practice.minutes': 'Activity duration (minutes)',
  'practice.scenario': 'Scenario for Noa',
  'practice.scenario.hint':
    'Optional. Only Noa reads it, when the person practises with it: the role Noa plays and what the person wants to practise. The person does not see it.',
  'practice.scenario.onlyNoa':
    'Only “Practise with Noa” activities have a scenario: change the type or delete the scenario to be able to publish.',
  'practice.add': 'Add activity',
  'practice.full': 'A lesson has at most {max, number} activities.',
  'practice.moveUp': 'Move activity {number, number} up',
  'practice.moveDown': 'Move activity {number, number} down',
  'practice.remove': 'Delete activity {number, number}',
  'practice.moved': '“{title}” moved to position {number, number} of {count, number}.',
  'practice.moved.untitled':
    'The untitled activity moved to position {number, number} of {count, number}.',

  'materials.title': 'Materials',
  'materials.empty': 'This lesson has no materials yet.',
  'materials.pdf': 'PDF',
  'materials.link': 'Link',
  'materials.number': 'Material {number, number}',
  'materials.name': 'Name',
  'materials.url': 'Link (https)',
  'materials.description': 'Description',
  'materials.description.hint':
    'Optional. What people will find in this material. Shown under its name.',
  'materials.file': 'Uploaded file.',
  'materials.file.waiting': 'No file: the upload did not finish.',
  'materials.file.again': 'Upload the PDF again',
  'materials.formats': 'PDF up to 50 MB.',
  'materials.addLink': 'Add external link',
  'materials.uploadPdf': 'Upload PDF',
  'materials.full': 'A lesson has at most {max, number} materials.',
  'materials.remove': 'Delete material {number, number}',
  'upload.sending': 'Uploading {name}',
  'upload.percent': '{percent, number, percent}',
  'upload.progress': 'Upload progress',
  'upload.scanning': 'Analysing {name}: we check that the file is safe.',
  'upload.cancel': 'Cancel',
  'upload.cancel.label': 'Cancel the upload of {name}',
  'upload.error.too-large': 'The file is larger than the maximum size.',
  'upload.error.empty': 'The file is empty.',
  'upload.error.wrong-type': 'The file is not in the format accepted here.',
  'upload.error.quota-exceeded':
    'No more files can be uploaded this month: the organization has reached its 20 GB limit.',
  'upload.error.infected':
    'The security check found a problem in the file, and it was not uploaded.',
  'upload.error.scan-failed': 'The file could not be checked. Try another one.',
  'upload.error.missing': 'The file did not arrive whole. Upload it again.',
  'upload.error.expired': 'The upload has expired. Upload the file again.',
  'upload.error.network': 'The connection was lost while uploading. Upload it again.',
  'upload.error.scan-timeout': 'Checking the file is taking too long. Upload it again later.',
  'upload.error.failed': 'The file could not be uploaded. Try again.',

  'reflection.title': 'Reflection exercise',
  'reflection.body':
    'Up to {max, number} moments: open questions that the person answers in writing. In the lesson, it comes after the video and the practice.',
  'reflection.intro': 'Introduction',
  'reflection.intro.hint':
    'Optional. Shown before the moments: what the exercise is for or how to do it.',
  'reflection.intro.unseen':
    'This introduction will not be seen: without moments, the lesson does not show the reflection exercise.',
  'reflection.empty': 'This lesson has no reflection exercise yet.',
  'reflection.moment': 'Moment {number, number}',
  'reflection.fixed':
    'This lesson has been published: you can correct the text of each moment, but not change their order, add or remove moments. Each person’s answers are kept by their position.',
  'reflection.add': 'Add moment',
  'reflection.full': 'A lesson has at most {max, number} moments.',
  'reflection.moveUp': 'Move moment {number, number} up',
  'reflection.moveDown': 'Move moment {number, number} down',
  'reflection.remove': 'Delete moment {number, number}',
  'reflection.moved': '“{prompt}” moved to position {number, number} of {count, number}.',
  'reflection.moved.untitled':
    'The moment with no text moved to position {number, number} of {count, number}.',

  'test.title': 'Test',
  'test.body':
    'Review questions of the lesson, each with four options and one correct answer. It has no pass mark, but it has to be submitted to complete the lesson: once it is submitted, the person sees the correct answers and the explanations, and can take it again. For a quiz that has to be passed, add an intermediate assessment after the lesson.',
  'test.empty': 'This lesson has no test yet.',
  'test.question': 'Question {number, number}',
  'test.prompt': 'Question text',
  'test.options': 'Options',
  'test.options.hint': 'Use the circle to mark the correct option.',
  'test.option': 'Option {number, number}',
  'test.correct': 'Correct',
  'test.correct.label': 'Option {number, number} is the correct one',
  'test.correct.none': 'Mark which option is correct.',
  'test.correct.pending':
    'You have not marked the correct option yet. You can go on, but it is needed to publish.',
  'test.correct.pending.announce': 'Question {number, number}: no correct option marked yet.',
  'test.explanation': 'Explanation',
  'test.explanation.hint': 'Optional. Shown after answering.',
  'test.add': 'Add question',
  'test.full': 'A test has at most {max, number} questions.',
  'test.remove': 'Delete question {number, number}',

  'assessment.intermediate': 'Intermediate assessment',
  'assessment.intermediate.body':
    "A graded quiz right after the lesson you choose in “Comes after”: it has to be passed to go on with the course. It has its own questions, which are not taken from the lesson's test.",
  'assessment.final': 'Final assessment',
  'assessment.final.body':
    "A graded quiz after the last lesson: it has to be passed to complete the course. It has its own questions, which are not taken from the lessons' tests.",
  'assessment.remove': 'Delete assessment',
  'assessment.title': 'Assessment title',
  'assessment.title.hint': 'For example, “Review of lessons 1 to 3”.',
  'assessment.minutes': 'Estimated time (minutes)',
  'assessment.after': 'Comes after',
  'assessment.after.hint':
    'Only one assessment after each lesson, and none after the last one when there is a final assessment.',
  'assessment.after.newVersion':
    "Moving it to another lesson changes the course's structure: when published, it will be a new version.",
  'assessment.after.taken': '{lesson} (already has an assessment)',
  'assessment.after.last': '{lesson} (the final assessment comes after it)',
  'assessment.after.unknown': 'Choose a lesson',
  'assessment.rules': 'To pass',
  'assessment.threshold': 'Pass mark',
  'assessment.threshold.value': '{value, number}% correct answers',
  'assessment.attempts': 'Attempts',
  'assessment.attempts.value': '{value, plural, one {# attempt} other {# attempts}}',
  'assessment.rules.hint':
    'They are the same for every assessment and are not changed in the editor.',
  'assessment.questions': 'Assessment questions',
  'assessment.questions.body':
    "Each with four options and one correct answer. People taking the course never see an assessment's correct answers.",
  'assessment.questions.empty': 'This assessment has no questions yet.',
  'assessment.questions.required':
    'Add at least one question: an assessment without questions cannot be published.',
  'assessment.questions.full': 'An assessment has at most {max, number} questions.',

  'save.cancel': 'Cancel',
  'save.now': 'Save changes',
  'save.state.saved': 'All changes are saved.',
  'save.state.savedAt': 'Changes saved at {at, time, short}.',
  'save.state.waiting': 'There are unsaved changes. They save on their own in a few seconds.',
  'save.state.saving': 'Saving…',
  'save.state.invalid': 'The changes could not be saved: review the marked fields.',
  'save.state.tooLarge':
    'The course is too large to save. Shorten texts or remove lessons or questions, and it will save on its own.',
  'save.state.retrying': 'The changes could not be saved. We will try again in a few seconds.',
  'save.state.stopped': 'The changes can no longer be saved.',

  'save.conflict': "Your save replaced someone else's changes.",
  'save.conflict.show': 'See the notice',

  'stopped.gone':
    'This draft no longer exists: it was published or discarded from another tab. Open the course again to keep editing it.',
  'stopped.notFound': 'This course no longer exists or you no longer have access to it.',
  'stopped.archived': 'This course has been archived and can no longer be edited.',
  'stopped.notAllowed': 'You cannot edit this course.',
  'stopped.error': 'The changes could not be saved.',
  'stopped.reopen': 'Open the course again',
  'reopen.title': 'Open the course again?',
  'reopen.body': 'The changes not saved will be lost: the course opens as it is saved now.',
  'reopen.confirm': 'Open again',

  'conflict.body':
    'While you were editing, someone else saved changes to this course, at {at, time, short}. Your last save replaced them: check that nothing is missing.',
  'conflict.dismiss': 'Got it',

  'issues.title':
    '{count, plural, one {There is one field to review} other {There are # fields to review}}',
  'issues.body': 'The draft was not saved with these values. Correct them and it saves on its own.',
  'issues.at': '{place}: {problem}',
  'issues.course': 'Course',

  'place.course.title': 'Course title',
  'place.course.description': 'Short description',
  'place.course.longDescription': 'Course description',
  'place.course.objectives': 'Course objectives',
  'place.course.language': 'Course language',
  'place.course.series': 'Series',
  'place.course.level': 'Level',
  'place.course.estimatedMinutes': 'Estimated time to complete',
  'place.course.lessons': 'Lessons',
  'place.lesson': 'Lesson {number, number}',
  'place.intermediate': 'Intermediate assessment',
  'place.final': 'Final assessment',
  'place.intermediate.after': 'Assessment after lesson {number, number}',
  'place.estimatedMinutes': 'Estimated time',
  'place.afterLessonKey': 'Comes after',
  'place.passThreshold': 'Pass mark',
  'place.maxAttempts': 'Attempts',
  'place.title': 'Title',
  'place.description': 'Description',
  'place.objectives': 'Objectives',
  'place.keyIdeas': 'Key ideas',
  'place.durationMinutes': 'Duration',
  'place.video': 'Video',
  'place.materials': 'Materials',
  'place.material': 'Material {number, number}',
  'place.name': 'Name',
  'place.url': 'Link',
  'place.reflection': 'Reflection exercise',
  'place.reflectionIntro': 'Reflection introduction',
  'place.practice': 'Practice',
  'place.activity': 'Activity {number, number}',
  'place.kind': 'Type',
  'place.instructions': 'Instructions',
  'place.noaScenario': 'Scenario for Noa',
  'place.moment': 'Moment {number, number}',
  'place.test': 'Test',
  'place.questions': 'Questions',
  'place.question': 'Question {number, number}',
  'place.prompt': 'Question text',
  'place.options': 'Options',
  'place.option': 'Option {number, number}',
  'place.correct': 'Correct answer',
  'place.explanation': 'Explanation',
  'place.separator': ' · ',

  'problem.required': 'This is missing.',
  'problem.too-long': 'It is too long.',
  'problem.too-few': 'Some items are missing.',
  'problem.too-many': 'There are too many items.',
  'problem.out-of-range': 'The value is out of the allowed range.',
  'problem.invalid': 'The value is not valid.',
  'problem.invalid-characters': 'It has characters that cannot be saved.',
  'problem.not-https': 'The link has to start with https://.',
  'problem.video-host': 'Only YouTube or Vimeo videos can be published.',
  'problem.only-for-noa': 'Only practice with Noa has a scenario.',
  'problem.duplicate-key': 'This item is repeated.',
  'problem.unknown-lesson': 'The lesson it follows no longer exists.',
  'problem.lesson-taken': 'Another assessment already follows this lesson.',
  'problem.after-last-lesson': 'It cannot follow the last lesson: the final assessment goes there.',
  'problem.series-and-level': 'The series and the level go together: set both or neither.',
  'problem.video-or-material-required': 'Some lesson has to have a video or a material.',
  'problem.file-not-clean': 'The file has not been checked yet or did not pass the check.',
  'problem.file-wrong-kind': 'The file is not of the kind expected here.',
  'problem.file-not-found': 'The file is no longer available.',
  'problem.reflection-fixed':
    'This lesson has been published: its moments cannot be reordered, added or removed.',
  'problem.level-taken': 'Another course of the series already has this level.',

  'actions.preview': 'Preview as an employee',
  'actions.preview.busy': 'Opening the preview…',
  'actions.publish': 'Publish',
  'actions.publish.checking': 'Checking…',
  'actions.publish.busy': 'Publishing…',
  'actions.discard': 'Discard draft',
  'preview.title': 'Preview',
  'preview.body':
    'This is how each lesson looks to a person taking the course. Nothing you answer here is saved.',
  'preview.marked':
    'You have previewed this version of the draft: if you change nothing, it can be published.',
  'preview.lessons': 'Lessons',
  'preview.none': 'This course has no lessons yet.',
  'preview.untitled': 'Untitled lesson',
  'preview.video.file': 'The uploaded video plays in the portal; the preview does not play it yet.',
  'preview.reflection': 'In the preview, answers are not saved.',
  'preview.materials.pdf': 'PDFs are downloaded in the portal.',
  'preview.noa':
    'In the portal, this activity only appears in companies that have Noa turned on, where people practise with Noa.',
  'preview.back': 'Back to the editor',
  'publishPreview.title': 'Preview the course before publishing it',
  'publishPreview.body':
    'Catalogue courses are published after seeing how a person will see them. Open it in the preview and publish it from there.',
  'publishPreview.confirm': 'Preview as an employee',
  'publish.created.title': 'Publish the course?',
  'publish.created.body.catalog':
    'It will be version 1. It will be available to activate for companies.',
  'publish.created.body.organization': 'It will be version 1. It will be available to assign.',
  'publish.newVersion.title': 'Publish a new version?',
  'publish.newVersion.body':
    "The course's structure has changed (its lessons, which of them have a video, its questions, assessments or language): it will be version {version, number}. Only new assignments get it; people who already have the course keep the version they had.",
  'publish.newVersion.confirm': 'Publish new version',
  'publish.correction.title': 'Publish a correction?',
  'publish.correction.body':
    "The course's structure does not change: it is a correction of version {version, number}. It reaches everyone who already has the course, without changing the attempts they have made.",
  'publish.correction.confirm': 'Publish correction',
  'publish.confirm': 'Publish',
  'publish.catalog': 'It applies to every company that has the course.',
  'publish.passkey': 'We will ask for your passkey to confirm it.',
  'publish.error.save':
    'To publish, the changes have to be saved first, and they could not be saved. Check the notice at the bottom and try again.',
  'publish.error.changed':
    'Someone else saved changes to this course meanwhile. Open the course again to see them.',
  'publish.error.changedDuringCheck':
    'The course changed while it was being checked. It has not been published: press “Publish” again.',
  'publish.error.gone':
    'This draft no longer exists: it was published or discarded from another tab. Open the course again to keep editing it.',
  'publish.error.generic': 'The course could not be published. Try again in a few minutes.',
  'publish.error.limit':
    'Your organization has already published the maximum number of new course versions today. The changes stay saved in the draft: try again tomorrow.',
  'preview.error': 'The preview could not be opened. Try again in a few minutes.',
  'discard.error': 'The draft could not be discarded. Try again in a few minutes.',
  'issues.publish.body':
    'The draft is saved, but it cannot be published yet: correct these fields and publish again.',
  'discard.title': 'Discard the draft?',
  'discard.body.new':
    'This course has never been published: it will be deleted with all its content.',
  'discard.body.published':
    'Changes not yet published will be lost. The course stays as it is published (version {version, number}).',
  'discard.confirm': 'Discard draft',
  'discard.busy': 'Discarding…',
  'done.published.created.catalog':
    'Course published: version 1. It can now be activated for companies.',
  'done.published.created.organization': 'Course published: version 1. It can now be assigned.',
  'done.published.newVersion': 'Version {version, number} published. Only new assignments get it.',
  'done.published.correction':
    'Correction published in version {version, number}: everyone who has the course sees it now.',
  'done.discarded.deleted':
    'Draft discarded. The course had never been published and has been deleted.',
  'done.discarded.kept':
    'Draft discarded. The course stays as it is published (version {version, number}).',
  'done.back': 'Back to the courses',
  'done.back.organization': 'Back to the programmes',
  'done.edit': 'Keep editing',

  'leave.title': 'Leave without saving?',
  'leave.body': 'Some changes have not been saved yet. If you leave now, they will be lost.',
  'leave.stay': 'Keep editing',
  'leave.confirm': 'Leave without saving',

  'confirm.cancel': 'Cancel',
  'confirm.lesson.title': 'Delete lesson {number, number}?',
  'confirm.lesson.body':
    '“{title}” will be deleted with its video, the practice, the reflection, its materials and the test.',
  'confirm.lesson.bodyUntitled':
    'This lesson will be deleted with its video, the practice, the reflection, its materials and the test.',
  'confirm.lesson.assessments':
    '{count, plural, one {The assessment that follows it will be deleted too.} other {The # assessments that follow it will be deleted too.}}',
  'confirm.lesson.published':
    'Whoever already has the course assigned keeps it: the change only reaches new assignments when you publish.',
  'confirm.lesson.confirm': 'Delete lesson',
  'confirm.assessment.title': 'Delete the assessment?',
  'confirm.assessment.body': '“{title}” will be deleted with its questions.',
  'confirm.assessment.bodyUntitled': 'This assessment will be deleted with its questions.',
  'confirm.assessment.confirm': 'Delete assessment',
  'confirm.question.title': 'Delete question {number, number}?',
  'confirm.question.body': 'The question will be deleted with its options and its explanation.',
  'confirm.question.confirm': 'Delete question',
  'confirm.material.title': 'Delete material {number, number}?',
  'confirm.material.body': 'It will be removed from the lesson.',
  'confirm.material.confirm': 'Delete material',
  'confirm.moment.title': 'Delete moment {number, number}?',
  'confirm.moment.body': 'The question of this moment will be deleted.',
  'confirm.moment.confirm': 'Delete moment',
  'confirm.practice.title': 'Delete activity {number, number}?',
  'confirm.practice.body': "The activity will be deleted from this lesson's practice.",
  'confirm.practice.confirm': 'Delete activity',
  'confirm.video.title': 'Remove the video?',
  'confirm.video.body':
    'The uploaded video will be removed from this lesson, with its captions. Then you can upload another one or add a YouTube or Vimeo link.',
  'confirm.video.confirm': 'Remove video',
  /** The title of a duplicate of a course, in the course's language (ADR 0009 §8). */
  'course.duplicateTitle': 'Copy of {title}',
} as const satisfies EditorCatalog
